/**
 * @file useStarterHints.ts
 * @description Подсказки стартового шаблона в отмеченных страницах окна
 * «Опубликовать» (phase19.28 этап А; решение 2026-10-24, uiux-starter-site
 * вопрос 2). Черновик страницы (`GET /admin/site/pages/:id`) читается один
 * раз на страницу при первой отметке; отпечаток в ключе — после правок
 * черновика страница проверяется заново. Сбой чтения — просто без
 * предупреждения: это подсказка, не запрет.
 */

import { useEffect, useState } from "react";

import { sitePages } from "../../../lib/api-site-pages";
import { starterHintsOfBlocks } from "../../../lib/site-builder/starter-hints";
import type { PublishPageRow, RowSelection } from "../../../lib/site-builder/page-publish-items";

/** pageId → подсказки, найденные в черновике с этим отпечатком. */
type Found = Record<string, { fingerprint: string; hints: string[] }>;

export function useStarterHints(rows: readonly PublishPageRow[], selection: Readonly<Record<string, RowSelection>>) {
  const [found, setFound] = useState<Found>({});
  const wanted = rows.filter((row) => selection[row.pageId]?.checked && !row.showOnly);
  const missing = wanted.filter((row) => found[row.pageId]?.fingerprint !== row.fingerprint);
  const key = missing.map((row) => `${row.pageId}:${row.fingerprint}`).join("|");

  useEffect(() => {
    if (missing.length === 0) return;
    let alive = true;
    for (const row of missing) {
      sitePages
        .findOne(row.pageId)
        .then((page) => starterHintsOfBlocks(page.blocks))
        .catch(() => [] as string[])
        .then((hints) => {
          if (alive) setFound((prev) => ({ ...prev, [row.pageId]: { fingerprint: row.fingerprint, hints } }));
        });
    }
    return () => {
      alive = false;
    };
    // Ключ — набор непроверенных страниц и их отпечатков.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  /** Подсказки страницы пункта (только у отмеченного, по актуальному отпечатку). */
  return (row: PublishPageRow): string[] => {
    const entry = found[row.pageId];
    return selection[row.pageId]?.checked && entry?.fingerprint === row.fingerprint ? entry.hints : [];
  };
}
